import { useEffect, useMemo, useState } from "react";
import { 
  Box, 
  Card, 
  Grid, 
  Stack, 
  styled, 
  useTheme,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Collapse,
  IconButton,
  InputAdornment,
  Skeleton,
  alpha,
  Checkbox,
  Chip,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { 
  FilterList, 
  ExpandMore, 
  Refresh, 
  CalendarToday 
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import { H3, H5, H6, Small, Tiny } from "components/Typography";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";

const StyledChart = styled(Chart)(({ theme }) => ({
  "& .apexcharts-tooltip": {
    boxShadow: "none",
  },
  "& .apexcharts-tooltip.apexcharts-theme-light": {
    border: "none",
    color: theme.palette.text.primary,
    borderRadius: 8,
  },
}));

// Styled components for filters
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'visible',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    cursor: 'pointer',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
}));

const StatusChip = styled(Typography)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  fontSize: '0.75rem',
  backgroundColor: statuscolor ? alpha(statuscolor, 0.1) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.2) : alpha(theme.palette.grey[500], 0.2)}`,
  display: 'inline-block',
}));

function formatCurrency(value) {
  if (value == null || isNaN(Number(value))) return "0";
  const num = Number(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(num);
}

function percent(val) {
  if (val == null || isNaN(Number(val))) return "0%";
  return `${Number(val).toFixed(1)}%`;
}

function getTopFiveByRevenue(products) {
  const list = Array.isArray(products) ? [...products] : [];
  return list
    .sort((a, b) => Number(b.total_revenue || 0) - Number(a.total_revenue || 0))
    .slice(0, 5);
}

function getTopProductChartColors(theme) {
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || "#23C657";
  return [
    primary,
    alpha(primary, 0.82),
    secondary,
    theme.palette.success?.main || "#27CE88",
    alpha(secondary, 0.65),
  ];
}

const TopProductsRevenueDonut = ({ products, loading }) => {
  const theme = useTheme();
  const topFive = useMemo(() => getTopFiveByRevenue(products), [products]);
  const chartColors = useMemo(() => getTopProductChartColors(theme), [theme]);

  const { series, labels, totalRevenue, sharePercents } = useMemo(() => {
    const revenues = topFive.map((p) => Number(p.total_revenue || 0));
    const total = revenues.reduce((sum, val) => sum + val, 0);
    const shares = revenues.map((val) => (total > 0 ? (val / total) * 100 : 0));
    return {
      series: revenues,
      labels: topFive.map((p) => p.product__name || "Unknown"),
      totalRevenue: total,
      sharePercents: shares,
    };
  }, [topFive]);

  const donutChart = useMemo(
    () => ({
      options: {
        chart: {
          background: "transparent",
          animations: {
            enabled: true,
            easing: "easeinout",
            speed: 750,
            animateGradually: { enabled: true, delay: 140 },
          },
        },
        labels,
        colors: chartColors,
        legend: { show: false },
        dataLabels: { enabled: false },
        stroke: {
          width: 3,
          colors: [theme.palette.background.paper],
        },
        states: {
          hover: { filter: { type: "lighten", value: 0.14 } },
          active: { filter: { type: "darken", value: 0.06 } },
        },
        theme: { mode: theme.palette.mode },
        tooltip: {
          fillSeriesColor: false,
          theme: theme.palette.mode === "dark" ? "dark" : "light",
          custom: ({ series: chartSeries, seriesIndex, w }) => {
            const value = chartSeries[seriesIndex];
            const label = w.globals.labels[seriesIndex];
            const color = w.config.colors[seriesIndex];
            const share =
              totalRevenue > 0 ? ((value / totalRevenue) * 100).toFixed(1) : "0.0";
            return [
              '<div style="padding:8px 12px;border-radius:8px;background:',
              color,
              ';color:#fff;font-weight:600;font-size:13px;box-shadow:0 4px 14px rgba(0,0,0,0.2);line-height:1.45;">',
              '<div style="font-size:12px;opacity:0.92;margin-bottom:2px;">',
              label,
              '</div>',
              '<div>',
              formatCurrency(value),
              '</div>',
              '<div style="font-size:11px;opacity:0.9;margin-top:2px;">',
              share,
              '% share</div></div>',
            ].join('');
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: { show: false },
                value: { show: false },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total Revenue",
                  fontSize: "11px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  formatter: () => formatCurrency(totalRevenue),
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              plotOptions: { pie: { donut: { size: "68%" } } },
            },
          },
        ],
      },
      series,
    }),
    [labels, series, chartColors, totalRevenue, theme]
  );

  if (loading) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={5} lg={4}>
          <Skeleton variant="circular" width={220} height={220} sx={{ mx: "auto" }} />
        </Grid>
        <Grid item xs={12} md={7} lg={8}>
          <Stack spacing={1.5}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: 1.5 }} />
            ))}
          </Stack>
        </Grid>
      </Grid>
    );
  }

  if (!topFive.length) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Small color="text.secondary">No product revenue data for the selected filters.</Small>
      </Box>
    );
  }

  return (
    <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
      <Grid item xs={12} md={5} lg={4}>
        <Box sx={{ maxWidth: 300, mx: "auto" }}>
          <StyledChart
            type="donut"
            height={280}
            options={donutChart.options}
            series={donutChart.series}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={7} lg={8}>
        <Stack spacing={1.25}>
          {topFive.map((row, idx) => {
            const revenue = Number(row.total_revenue || 0);
            const share = sharePercents[idx] ?? 0;
            const sliceColor = chartColors[idx % chartColors.length];
            return (
              <Stack
                key={`${row.product_id ?? row.product__sku}-${idx}`}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(sliceColor, 0.12)}`,
                  bgcolor: alpha(sliceColor, 0.04),
                  transition:
                    "background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(sliceColor, 0.1),
                    transform: "translateX(3px)",
                    boxShadow: `0 4px 12px ${alpha(sliceColor, 0.15)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    bgcolor: sliceColor,
                    boxShadow: `0 0 0 3px ${alpha(sliceColor, 0.2)}`,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, lineHeight: 1.3 }}
                    noWrap
                    title={row.product__name}
                  >
                    {row.product__name}
                  </Typography>
                  <Tiny color="text.secondary" sx={{ display: "block" }}>
                    {formatCurrency(revenue)}
                  </Tiny>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontVariantNumeric: "tabular-nums",
                    minWidth: 44,
                    textAlign: "right",
                  }}
                >
                  {share.toFixed(1)}%
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Grid>
    </Grid>
  );
};

const InfoStat = ({ title, value, subtitle, color = "primary" }) => {
  const theme = useTheme();
  return (
    <Card sx={{ p: 1.5, height: "100%" }}>
      <Stack spacing={0.5}>
        <Tiny sx={{ color: "text.primary", fontWeight: 600 }}>{title}</Tiny>
        <H3>{value}</H3>
        {subtitle ? (
          <Small color={theme.palette[color]?.main || theme.palette.text.secondary}>{subtitle}</Small>
        ) : null}
      </Stack>
    </Card>
  );
};

const useLastNDays = (n = 7) => {
  return useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (n - 1));
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { startDate: fmt(start), endDate: fmt(end) };
  }, [n]);
};

const AnalyticsDashboard = () => {
  const theme = useTheme();
     const { setPageTitle } = usePageTitle();
  const isCompanyUser = apiService.isCompanyUser?.();
  const companyId = apiService.getCompanyId?.();
  const companyDetails = apiService.getCompanyDetails?.();
  useEffect(() => {
        setPageTitle('Dashboard');
    }, [setPageTitle]);
  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [orderType, setOrderType] = useState('all'); // 'all', 'normal', 'bulk'
  const [companies, setCompanies] = useState([]);
  const [companiesMenuOpen, setCompaniesMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(
    isCompanyUser && companyId ? [String(companyId)] : []
  );
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [salesAnalyticsType, setSalesAnalyticsType] = useState("daily");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Last 30 days
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [useDateFilter, setUseDateFilter] = useState(true); // New state to control date filter usage
  const getDefaultFilters = () => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return {
      fromDate: date.toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      company: isCompanyUser && companyId ? [String(companyId)] : [],
      category: 'all',
      orderType: 'all',
      analyticsType: 'daily',
    };
  };
  const [appliedFilters, setAppliedFilters] = useState(() => getDefaultFilters());

  const [loading, setLoading] = useState(true);
  const [salesOverview, setSalesOverview] = useState({});
  const [activityMetrics, setActivityMetrics] = useState({ total_revenue: 0, total_activity: 0, activities: [] });
  const [byProduct, setByProduct] = useState({ category_performance: [], category_growth: [] });
  const [topProducts, setTopProducts] = useState([]);
  const [farmersAnalyticsData, setFarmersAnalyticsData] = useState([]);
  const [farmersAnalyticsLoading, setFarmersAnalyticsLoading] = useState(true);
  const [farmersAnalyticsError, setFarmersAnalyticsError] = useState("");
  const [farmersAnalyticsType, setFarmersAnalyticsType] = useState("monthly");
  const [error, setError] = useState("");
  const salesAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];
  const farmersAnalyticsTypeOptions = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // Build query string with filters for analytics APIs
  const buildQueryString = (filters = appliedFilters) => {
    const params = new URLSearchParams();
    const {
      fromDate,
      toDate,
      company,
      category,
      orderType: selectedOrderType,
      analyticsType,
    } = filters;

    let companyParam = "";
    if (isCompanyUser) {
      companyParam = String(companyId || "");
    } else if (Array.isArray(company)) {
      companyParam = company.length > 0 ? company.join(",") : "";
    } else if (company && company !== "all") {
      companyParam = company;
    }
    if (companyParam) {
      params.set("company", companyParam);
    }
    if (category && category !== "all") {
      params.set("category", category);
    } else {
      params.set("category", "");
    }

    const apiOrderType = selectedOrderType === "normal" ? "app" : selectedOrderType;
    params.set("order_type", apiOrderType);
    params.set("analytics_type", analyticsType || salesAnalyticsType);

    if (fromDate) {
      params.set("from_date", fromDate);
    }
    if (toDate) {
      params.set("to_date", toDate);
    }

    return `?${params.toString()}`;
  };

  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyDetails) {
        setCompanies([companyDetails]);
        return;
      }
      const response = await apiService.get(API_ENDPOINTS.GET_company);
      if (response?.success) {
        setCompanies(response?.data?.Company || []);
      } else {
        setCompanies([]);
      }
    } catch (err) {
      console.error("Error fetching companies:", err);
      setCompanies([]);
    }
  };

  const getCategoryCompanyValue = (companySelection) => {
    if (isCompanyUser && companyId) return String(companyId);
    if (Array.isArray(companySelection)) {
      return companySelection.length === 1 ? companySelection[0] : "all";
    }
    return companySelection || "all";
  };

  const fetchCategories = async (companyValue = "all") => {
    try {
      let response;
      if (companyValue === "all") {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem("user_id");
        response = await apiService.get(
          `${API_ENDPOINTS.GET_product_category}?company=${companyValue}&user_id=${userId}`
        );
      }
      if (response?.success) {
        setCategories(response?.data?.ProductCategory || []);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  const fetchFarmersAnalytics = async (
    filters = appliedFilters,
    analyticsType = farmersAnalyticsType
  ) => {
    setFarmersAnalyticsLoading(true);
    setFarmersAnalyticsError("");
    try {
      const qs = buildQueryString({ ...filters, analyticsType });
      const payload = await apiService.get(
        API_ENDPOINTS.GET_farmer_engagement_farmers_analytics + qs
      );
      const normalizedList = Array.isArray(payload?.data?.analytics)
        ? payload.data.analytics
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.analytics)
            ? payload.analytics
            : [];
      setFarmersAnalyticsData(normalizedList);
    } catch (err) {
      setFarmersAnalyticsError(
        typeof err === "string" ? err : err?.message || "Failed to load farmers analytics"
      );
      setFarmersAnalyticsData([]);
    } finally {
      setFarmersAnalyticsLoading(false);
    }
  };

  const fetchAllData = async (filters = appliedFilters) => {
    setLoading(true);
    setError("");
    try {
      const qs = buildQueryString(filters);
      const [activityRes, overviewRes, byProductRes, byCategoryRes] = await Promise.all([
        apiService.get(API_ENDPOINTS.GET_farmer_engagement_activity_metrics + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_overview + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_product + qs),
        apiService.get(API_ENDPOINTS.GET_sales_revenue_by_category + qs),
        fetchFarmersAnalytics(filters, farmersAnalyticsType),
      ]);
      setSalesOverview(overviewRes?.data || {});

      const metricsCandidates = [
        activityRes?.data?.data,
        activityRes?.data,
        activityRes,
      ].filter(Boolean);

      const pickMetricsPayload = metricsCandidates.find((payload) => {
        if (Array.isArray(payload?.activities) && payload.activities.length > 0) return true;
        return payload?.total_revenue != null || payload?.total_activity != null;
      }) || {};
      const rawActivities = pickMetricsPayload?.activities;
      const metricsActivities = Array.isArray(rawActivities)
        ? rawActivities
        : (rawActivities && typeof rawActivities === "object" ? Object.values(rawActivities) : []);
      const rawTotalRevenue = pickMetricsPayload?.total_revenue ?? 0;
      const derivedTotalActivity = pickMetricsPayload?.total_activity != null
        ? Number(pickMetricsPayload.total_activity || 0)
        : metricsActivities.reduce((sum, item) => sum + Number(item?.value || 0), 0);
      setActivityMetrics({
        total_revenue: Number(rawTotalRevenue || 0),
        total_activity: derivedTotalActivity,
        activities: metricsActivities,
      });

      // category performance/growth comes from category endpoint
      setByProduct({
        category_performance: Array.isArray(byCategoryRes?.data?.category_performance)
          ? byCategoryRes.data.category_performance
          : Array.isArray(byCategoryRes?.category_performance)
            ? byCategoryRes.category_performance
            : [],
        category_growth: Array.isArray(byCategoryRes?.data?.category_growth)
          ? byCategoryRes.data.category_growth
          : Array.isArray(byCategoryRes?.category_growth)
            ? byCategoryRes.category_growth
            : [],
      });
      // top products is an array from top-products endpoint
      setTopProducts(Array.isArray(byProductRes?.data) ? byProductRes.data : []);
    } catch (err) {
      console.error(err);
      setError(typeof err === "string" ? err : err?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchCompanies();
      const initialCompany = isCompanyUser && companyId ? [String(companyId)] : selectedCompany;
      await fetchCategories(getCategoryCompanyValue(initialCompany));
    };
    initializeFilters();
  }, []);

  useEffect(() => {
    setAppliedFilters((prev) => {
      if (prev?.analyticsType === salesAnalyticsType) return prev;
      return { ...prev, analyticsType: salesAnalyticsType };
    });
  }, [salesAnalyticsType]);

  const handleApplyFilters = () => {
    const nextAppliedFilters = {
      fromDate: useDateFilter ? startDate : "",
      toDate: useDateFilter ? endDate : "",
      company: isCompanyUser && companyId ? [String(companyId)] : selectedCompany,
      category: selectedCategory,
      orderType,
      analyticsType: salesAnalyticsType,
    };
    setAppliedFilters(nextAppliedFilters);
  };

  const handleCompanyChange = async (event) => {
    const value = event.target.value;
    const companyIds = typeof value === "string" ? value.split(",") : value;
    setSelectedCompany(companyIds);
    setSelectedCategory("all");
    await fetchCategories(getCategoryCompanyValue(companyIds));
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleOrderTypeChange = (event) => {
    setOrderType(event.target.value);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleResetFilters = () => {
    const defaults = getDefaultFilters();
    setStartDate(defaults.fromDate);
    setEndDate(defaults.toDate);
    setOrderType(defaults.orderType);
    setSalesAnalyticsType(defaults.analyticsType);
    setSelectedCompany(defaults.company);
    setSelectedCategory(defaults.category);
    setUseDateFilter(true); // keep existing behavior
    setAppliedFilters(defaults);
    setTimeout(() => fetchCategories(getCategoryCompanyValue(defaults.company)), 0);
  };

  const handleClearDateFilter = () => {
    setUseDateFilter(false);
    setStartDate('');
    setEndDate('');
  };

  const handleEnableDateFilter = () => {
    setUseDateFilter(true);
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    setStartDate(defaultStartDate.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
  };

  const getOrderTypeLabel = (type) => {
    switch (type) {
      case 'normal': return 'Orders';
      case 'bulk': return 'Bulk Orders';
      case 'all': return 'All Orders';
      default: return 'All Orders';
    }
  };

  const isFiltersActive = () => {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    return orderType !== 'all' || 
           !useDateFilter ||
           (useDateFilter && (startDate !== defaultStartDate.toISOString().split('T')[0] || 
            endDate !== new Date().toISOString().split('T')[0]));
  };

  const ordersByStatusChart = useMemo(() => {
    const statuses = salesOverview?.orders_by_status || [];
    const labels = statuses.map((s) => s.status);
    const series = statuses.map((s) => Number(s.count || 0));
    return {
      options: {
        chart: { background: "transparent" },
        labels,
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.main, theme.palette.grey[500], theme.palette.secondary.main],
        legend: { show: true, position: "bottom", fontSize: "13px", fontWeight: 500 },
              theme: { mode: theme.palette.mode },
      tooltip: {
        fillSeriesColor: false,
        theme: 'dark',
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex];
          const label = w.globals.labels[seriesIndex];
          const color = w.config.colors[seriesIndex]; // 👈 get the same color as the slice
          return `
            <div style="
              padding: 6px 10px;
              border-radius: 6px;
              background: ${color};
              color: #fff;
              font-weight: 600;
              font-size: 13px;
              box-shadow: 0 2px 6px rgba(0,0,0,0.25);
            ">
              ${label}: ${value}
            </div>
          `;
        },
      },


        plotOptions: {
          pie: {
            donut: {
              size: "60%",
              labels: { show: true, total: { show: true, label: "Orders" } },
            },
          },
        },
      },
      series,
    };
  }, [salesOverview, theme]);

  const dailyRevenueChart = useMemo(() => {
    const salesAnalytics = salesOverview?.sales_analytics || [];
    
    // Use the pre-aggregated data directly from API
    const chartData = salesAnalytics.map((item) => ({
      label: item.label || "",
      revenue: Number(item.revenue || 0),
      orders: Number(item.orders || 0),
    }));

    // Data is already aggregated from API, just use it directly
    const categories = chartData.map((d) => d.label);
    const revenue = chartData.map((d) => d.revenue);
    const orders = chartData.map((d) => d.orders);

    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4, hover: { size: 6 } },
        colors: [theme.palette.primary.main, theme.palette.info.main],
        xaxis: {
          type: "category",
          categories,
          labels: { style: { colors: theme.palette.text.disabled, fontFamily: theme.typography.fontFamily, fontWeight: 500 } },
        },
        yaxis: [{
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Revenue" },
        }, {
          opposite: true,
          labels: { style: { colors: theme.palette.text.secondary } },
          title: { text: "Orders" },
        }],
        theme: { mode: theme.palette.mode },
        tooltip: {
          shared: false,
          intersect: true,
          custom: ({ dataPointIndex }) => {
            const point = chartData[dataPointIndex] || { label: "", revenue: 0, orders: 0 };
            return `
              <div style="padding:8px 10px;border-radius:6px;background:${theme.palette.background.paper};border:1px solid ${theme.palette.divider};min-width:130px;">
                <div style="font-size:12px;font-weight:600;margin-bottom:4px;color:${theme.palette.text.primary};">${point.label}</div>
                <div style="font-size:12px;color:${theme.palette.text.secondary};">Revenue: ${formatCurrency(point.revenue)}</div>
                <div style="font-size:12px;color:${theme.palette.text.secondary};">Orders: ${point.orders} orders</div>
              </div>
            `;
          },
        },
      },
      series: [
        { name: "Revenue", type: "area", data: revenue },
        { name: "Orders", type: "line", data: orders },
      ],
    };
  }, [salesOverview, theme]);

const categoryRevenueChart = useMemo(() => {
  const DEFAULT_CATEGORY_LIMIT = 10;
  const perf = byProduct?.category_performance || [];
  const activities = Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : [];
  const hasCategoryData = perf.length > 0;
  const limitedPerf = hasCategoryData ? perf.slice(0, DEFAULT_CATEGORY_LIMIT) : [];
  const limitedActivities = hasCategoryData ? [] : activities.slice(0, DEFAULT_CATEGORY_LIMIT);
  const categories = hasCategoryData
    ? limitedPerf.map((p) => p.category || "N/A")
    : limitedActivities.map((a) => a.label || "N/A");
  const revenue = hasCategoryData
    ? limitedPerf.map((p) => Number(p.total_revenue || 0))
    : limitedActivities.map((a) => Number(a.value || 0));
  const quantity = hasCategoryData ? limitedPerf.map((p) => Number(p.total_quantity || 0)) : [];

  return {
    options: {
      chart: {
        background: "transparent",
        toolbar: { show: false },
      },
      stroke: {
        width: [0, 3],
        curve: "smooth",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "45%",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: { enabled: false },
      colors: [theme.palette.primary.main, theme.palette.warning.main],
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 4,
        padding: { left: 10, right: 10 },
      },
      xaxis: {
        categories,
        tickPlacement: "on",
        labels: {
          show: true,
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          formatter: (value) =>
            value && value.length > 15 ? `${value.slice(0, 15)}...` : value,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: hasCategoryData ? [
        {
          title: { text: "Revenue (₹)" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
            formatter: (val) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val),
          },
        },
        {
          opposite: true,
          title: { text: "Quantity" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ] : [
        {
          title: { text: "Count" },
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
      ],
      legend: {
        position: "bottom",
        labels: { colors: theme.palette.text.secondary },
        fontSize: "12px",
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (value, { dataPointIndex }) => {
            // Show full category name in tooltip
            return categories[dataPointIndex] || value;
          },
        },
        y: {
          formatter: (val, { seriesIndex }) =>
            !hasCategoryData ? `${val}` : (seriesIndex === 0 ? formatCurrency(val) : `${val}`),
        },
      },
      theme: { mode: theme.palette.mode },
      responsive: [
        {
          breakpoint: 992,
          options: {
            plotOptions: { bar: { columnWidth: "60%" } },
            xaxis: {
              labels: {
                rotate: -60,
                style: { fontSize: "10px" },
                formatter: (value) =>
                  value && value.length > 10 ? `${value.slice(0, 10)}...` : value,
              },
            },
            legend: { fontSize: "10px" },
          },
        },
        {
          breakpoint: 576,
          options: {
            plotOptions: { bar: { columnWidth: "80%" } },
            xaxis: {
              labels: {
                rotate: -90,
                style: { fontSize: "9px" },
                formatter: (value) =>
                  value && value.length > 8 ? `${value.slice(0, 8)}...` : value,
              },
            },
            legend: { position: "top" },
          },
        },
      ],
    },
    series: hasCategoryData
      ? [
          { name: "Revenue", type: "column", data: revenue },
          { name: "Quantity", type: "line", data: quantity },
        ]
      : [
          { name: "Activity", type: "column", data: revenue },
        ],
  };
}, [byProduct, activityMetrics, theme]);




  const categoryGrowthChart = useMemo(() => {
    const growth = byProduct?.category_growth || [];
    const categories = Array.from(new Set(growth.map((g) => (g.month || "").slice(0, 7))));
    const categoryNames = Array.from(new Set(growth.map((g) => g.product__category__name)));
    const series = categoryNames.map((name) => ({
      name,
      data: categories.map((m) => {
        const row = growth.find((g) => g.product__category__name === name && (g.month || "").startsWith(m));
        return Number(row?.revenue || 0);
      }),
    }));
    return {
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth", width: 2 },
        colors: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.secondary.main, theme.palette.error.main],
        xaxis: { categories, labels: { style: { colors: theme.palette.text.secondary } } },
        yaxis: { labels: { style: { colors: theme.palette.text.secondary } }, title: { text: "Revenue" } },
        legend: { position: "bottom" },
        theme: { mode: theme.palette.mode },
        tooltip: { y: { formatter: (val) => formatCurrency(val) } },
      },
      series,
    };
  }, [byProduct, theme]);

  const farmersAnalyticsChart = useMemo(() => {
    const normalizedRows = Array.isArray(farmersAnalyticsData) ? farmersAnalyticsData : [];
    const analyticsRows = normalizedRows.filter((row) => row && typeof row === "object");
    const categories = analyticsRows.map((item, index) => item?.label || `Item ${index + 1}`);
    const totalFarmers = analyticsRows.map((item) => Number(item?.total_farmers || 0));
    const activeFarmers = analyticsRows.map((item) => Number(item?.active_farmers || 0));
    const newRegistrations = analyticsRows.map((item) => Number(item?.new_registrations || 0));
    const repeatOrderingFarmers = analyticsRows.map((item) =>
      Number(item?.repeat_ordering_farmers || 0)
    );

    return {
      hasData: categories.length > 0,
      options: {
        chart: { background: "transparent", toolbar: { show: false } },
        dataLabels: { enabled: false },
        colors: [
          theme.palette.primary.main,
          theme.palette.success.main,
          theme.palette.success.dark || theme.palette.success.main,
          theme.palette.secondary.main,
        ],
        legend: {
          show: true,
          position: "bottom",
          labels: { colors: theme.palette.text.secondary },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "58%",
          },
        },
        grid: {
          borderColor: theme.palette.divider,
          strokeDashArray: 4,
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: theme.palette.text.secondary, fontSize: "12px" },
          },
        },
        yaxis: {
          labels: { style: { colors: theme.palette.text.secondary, fontSize: "12px" } },
        },
        tooltip: {
          shared: false,
          intersect: true,
          y: {
            formatter: (val) => `${Number(val || 0)}`,
          },
        },
        theme: { mode: theme.palette.mode },
      },
      series: [
        { name: "Total Farmers", data: totalFarmers },
        { name: "Active Farmers", data: activeFarmers },
        { name: "New Registrations", data: newRegistrations },
        { name: "Repeat Ordering Farmers", data: repeatOrderingFarmers },
      ],
    };
  }, [farmersAnalyticsData, theme]);

  return (
    <Box p={2}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" mb={2} gap={1}>
        <H5>Analytics Dashboard</H5>
        
      </Stack>

      {error ? (
        <Card sx={{ p: 2, mb: 2 }}>
          <Small color="error.main">{error}</Small>
        </Card>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {/* Filters & Search */}
          <FilterContainer sx={{ mb: 0 }}>
            <FilterHeader>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <FilterList color="primary" />
                  <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Filters & Search
                  </Typography>
                  {useDateFilter && startDate && endDate && (
                    <>
                      <StatusChip statuscolor={theme.palette.success.main} sx={{ ml: 1 }}>
                        Active
                      </StatusChip>
                      <StatusChip statuscolor={theme.palette.info.main} sx={{ ml: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-bottom" }} />
                        {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </StatusChip>
                    </>
                  )}
                </Box>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Box>
            </FilterHeader>

            <Collapse in={showFilters}>
              <FilterContent>
                {/* <Typography variant="caption" sx={{ mb: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
                  Date Range Filter
                </Typography> */}
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.5,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                          lg: "repeat(5, minmax(0, 1fr))",
                        },
                        alignItems: "center",
                        "& > *": {
                          minWidth: 0,
                        },
                        "& .MuiFormControl-root, & .MuiTextField-root": {
                          minWidth: { xs: "100%", sm: 0 },
                        },
                      }}
                    >
                      <DatePickerField
                        fullWidth
                        label="From Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          setUseDateFilter(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          max: endDate || new Date().toISOString().split('T')[0],
                        }}
                        size="small"
                      />
                      <DatePickerField
                        fullWidth
                        label="To Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          setUseDateFilter(true);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: startDate || '',
                          max: new Date().toISOString().split('T')[0],
                        }}
                        size="small"
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel id="analytics-companies-label">Company</InputLabel>
                        <Select
                          labelId="analytics-companies-label"
                          multiple
                          value={selectedCompany}
                          label="Company"
                          open={companiesMenuOpen}
                          onOpen={() => setCompaniesMenuOpen(true)}
                          onClose={() => setCompaniesMenuOpen(false)}
                          onChange={handleCompanyChange}
                          disabled={isCompanyUser}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 320 },
                            },
                          }}
                          renderValue={(selected) => {
                            if (!selected?.length) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  All Companies
                                </Typography>
                              );
                            }
                            return (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((id) => {
                                  const company = companies.find((c) => String(c.id) === String(id));
                                  return (
                                    <Chip
                                      key={id}
                                      label={company?.name || id}
                                      size="small"
                                      sx={{ maxWidth: 160 }}
                                    />
                                  );
                                })}
                              </Box>
                            );
                          }}
                        >
                          {companies.map((company) => {
                            const companyIdStr = String(company.id);
                            const checked = Array.isArray(selectedCompany) && selectedCompany.includes(companyIdStr);
                            return (
                              <MenuItem key={company.id} value={companyIdStr}>
                                <Checkbox checked={checked} size="small" sx={{ py: 0 }} />
                                <ListItemText primary={company.name} />
                              </MenuItem>
                            );
                          })}
                          {!isCompanyUser && companies.length > 0 && (
                            <ListSubheader
                              component="div"
                              disableSticky
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 1,
                                backgroundColor: "background.paper",
                                borderTop: "1px solid",
                                borderColor: "divider",
                                py: 1,
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCompaniesMenuOpen(false);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </ListSubheader>
                          )}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={selectedCategory}
                          label="Category"
                          onChange={handleCategoryChange}
                        >
                          <MenuItem value="all">All Categories</MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth size="small">
                        <InputLabel>Order Type</InputLabel>
                        <Select
                          value={orderType}
                          label="Order Type"
                          onChange={handleOrderTypeChange}
                        >
                          <MenuItem value="all">All Orders</MenuItem>
                          <MenuItem value="normal">App Order</MenuItem>
                          <MenuItem value="bulk">Bulk Order</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
                      <ActionButton
                        sx={{
                          minWidth: 140,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          },
                        }}
                        variant="contained"
                        startIcon={<FilterList />}
                        onClick={handleApplyFilters}
                      >
                        Apply
                      </ActionButton>
                      <ActionButton
                        sx={{
                          minWidth: 140,
                          borderColor: theme.palette.error.main,
                          color: theme.palette.error.main,
                          '&:hover': {
                            borderColor: theme.palette.error.dark,
                            backgroundColor: alpha(theme.palette.error.main, 0.05),
                          },
                        }}
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleResetFilters}
                      >
                        Reset
                      </ActionButton>
                    </Box>
                  </Grid>
                </Grid>
              </FilterContent>
            </Collapse>
          </FilterContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <InfoStat title="Total Revenue" value={formatCurrency(activityMetrics?.total_revenue)} />
        </Grid>
       
        {(Array.isArray(activityMetrics?.activities) ? activityMetrics.activities : []).map((item, idx) => (
          <Grid item xs={12} sm={6} md={2} key={`${item.label || "activity"}-${idx}`}>
            <InfoStat title={item.label || "Activity"} value={item.value ?? 0} />
          </Grid>
        ))}

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={1}
            >
              <H6>Sales Analytics</H6>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>analytics_type</InputLabel>
                <Select
                  value={salesAnalyticsType}
                  label="analytics_type"
                  onChange={(event) => setSalesAnalyticsType(event.target.value)}
                >
                  {salesAnalyticsTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <StyledChart type="line" height={300} options={dailyRevenueChart.options} series={dailyRevenueChart.series} />
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, height: "100%" }}>
            <H6>Orders by Status</H6>
            <StyledChart type="donut" height={300} options={ordersByStatusChart.options} series={ordersByStatusChart.series} />
          </Card>
        </Grid>

        {/* <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <H6>{(byProduct?.category_performance || []).length > 0 ? "Revenue and Quantity by Category" : "Activity by Type"}</H6>
            <StyledChart type="line" height={340} options={categoryRevenueChart.options} series={categoryRevenueChart.series} />
          </Card>
        </Grid> */}

        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <H6>Top Products by Sales</H6>
            <Box mt={1}>
              <TopProductsRevenueDonut products={topProducts} loading={loading} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={1}
            >
              <H6>Farmers Analytics</H6>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Analytics type</InputLabel>
                <Select
                  value={farmersAnalyticsType}
                  label="analytics_type"
                  onChange={(event) => {
                    const nextType = event.target.value;
                    setFarmersAnalyticsType(nextType);
                    fetchFarmersAnalytics(appliedFilters, nextType);
                  }}
                >
                  {farmersAnalyticsTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Box mt={1}>
              {farmersAnalyticsLoading ? (
                <Skeleton variant="rounded" height={300} sx={{ borderRadius: 1.5 }} />
              ) : farmersAnalyticsError ? (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.error.main, 0.04),
                    border: `1px dashed ${alpha(theme.palette.error.main, 0.2)}`,
                  }}
                >
                  <Small color="error.main">{farmersAnalyticsError}</Small>
                </Box>
              ) : farmersAnalyticsChart.hasData ? (
                <StyledChart
                  key={`farmers-${farmersAnalyticsType}-${appliedFilters.fromDate}-${appliedFilters.toDate}-${appliedFilters.orderType}-${appliedFilters.category}-${appliedFilters.company}`}
                  type="bar"
                  height={320}
                  options={farmersAnalyticsChart.options}
                  series={farmersAnalyticsChart.series}
                />
              ) : (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <Small color="text.secondary">No farmers analytics data available.</Small>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard; 